import type { FC } from 'react';

import type { ObjectType } from '@iconik-io/iconik-api/src/types/object_types';
import { Icon } from '@iconik-io/uad';

import type { ObjectWithId } from '../../../types/common';
import ControlsModal from '../../../ui/controls_modal/controls_modal';
import {
  addObjectsType,
  formatObjectDefinitionLabel,
} from '../../../utils/object_type';
import withContextItems from '../context_items_hoc';
import type {
  ContextItemsHocProps,
  ModalComponentProps,
} from '../modals_manager/types';

import styles from './delete_objects.module.css';

const getSubclipMessage = (hasSubclips: boolean, items: ObjectWithId[]) => {
  if (!hasSubclips) {
    return null;
  }
  const subclipMessage = `This segment has one or more subclips. Deleting the segment will also delete the subclips.`;

  const subclipMessageMultiple = `Selected segments have one or more subclips. Deleting the segments will also delete the subclips.\n`;

  return items.length > 1 ? subclipMessageMultiple : subclipMessage;
};

const getSubjobMessage = (hasSubjobs: boolean, items: ObjectWithId[]) => {
  if (!hasSubjobs) {
    return null;
  }
  const subjobMessage =
    'This job contains subjobs. Deleting this job will also delete the subjobs.';

  const subjobMessageMultiple =
    'Some of the selected jobs contain subjobs. Deleting those jobs will also delete their subjobs.';

  return items.length > 1 ? subjobMessageMultiple : subjobMessage;
};

type Props = {
  objectType: ObjectType;
  entity?: string;
  deleteEntityByIds: (ids: string[], entity?: string) => Promise<unknown>;
  additionalBody?: string;
} & ModalComponentProps &
  ContextItemsHocProps;

const DeleteObjectsModalComponent: FC<Props> = (props) => {
  const {
    isOpen,
    items,
    onCancel,
    objectType,
    onConfirm,
    entity,
    additionalBody,
  } = props;
  const objectTypedItems = addObjectsType(items, objectType);

  const hasSubjobs = items.some((item) => item.has_children);
  const headerText = formatObjectDefinitionLabel(
    objectTypedItems,
    ({ objectName, firstItemTitle, count }) => ({
      single: `Delete ${objectName}${
        firstItemTitle ? ':' : ''
      } ${firstItemTitle}`,
      multiple: `Delete ${objectName} (${count})`,
    }),
  );
  const bodyText = formatObjectDefinitionLabel(
    objectTypedItems,
    ({ objectName, firstItemTitle }) => {
      const lowerCaseName = objectName.toLowerCase();

      return {
        single: firstItemTitle
          ? `Are you sure you want to delete the selected ${lowerCaseName} "${firstItemTitle}"?`
          : `Are you sure you want to delete this ${lowerCaseName}?`,
        multiple: `Are you sure you want to delete the selected ${lowerCaseName}?`,
      };
    },
  );

  const hasSubclips = items.some((item) => item.subclip_id);

  const subclipsMessage = getSubclipMessage(hasSubclips, items);
  const subjobsMessage = getSubjobMessage(hasSubjobs, items);
  return (
    <ControlsModal
      isOpen={isOpen}
      headerText={headerText}
      confirmButtonText="Delete"
      onCancel={onCancel}
      onConfirm={() => {
        const ids = items.map((item) => item.id);

        return props.deleteEntityByIds(ids, entity).then(onConfirm);
      }}
      variant="danger"
    >
      {subclipsMessage ? (
        <p className={styles.subclip_message}>{subclipsMessage}</p>
      ) : null}
      {subjobsMessage ? (
        <div className={styles.subjob_warning}>
          <Icon name="message_warning" className={styles.icon_warning} />
          <p>{subjobsMessage}</p>
        </div>
      ) : null}
      {additionalBody ? <p>{additionalBody}</p> : null}
      {bodyText}
    </ControlsModal>
  );
};

const DeleteObjectsModal = withContextItems(DeleteObjectsModalComponent);

export default DeleteObjectsModal;
